/** Query keys that would put a usable password in an address bar, history, or log. */
export function isPasswordQueryKey(key: string): boolean {
  return /^(password|passwd|pwd)$/i.test(key);
}

/** Drop password query keys from a path or `?search` string. Email and other params stay. */
export function stripPasswordQuery(path: string): string {
  const hashAt = path.indexOf("#");
  const hash = hashAt >= 0 ? path.slice(hashAt) : "";
  const withoutHash = hashAt >= 0 ? path.slice(0, hashAt) : path;
  const queryAt = withoutHash.indexOf("?");
  if (queryAt < 0) return path;
  const params = new URLSearchParams(withoutHash.slice(queryAt + 1));
  let removed = false;
  for (const key of [...params.keys()]) {
    if (!isPasswordQueryKey(key)) continue;
    params.delete(key);
    removed = true;
  }
  const redirect = params.get("redirect");
  if (redirect) {
    const cleaned = stripPasswordQuery(redirect);
    if (cleaned !== redirect) {
      params.set("redirect", cleaned);
      removed = true;
    }
  }
  if (!removed) return path;
  const rest = params.toString();
  return `${withoutHash.slice(0, queryAt)}${rest ? `?${rest}` : ""}${hash}`;
}

/** In-app path after a successful password sign-in. */
export function loginLandingPath(forcePasswordReset: boolean, search = "", stored = ""): string {
  if (forcePasswordReset) return "/change-password";
  const stripped = stripPasswordQuery(search.startsWith("?") ? search : `?${search}`).replace(/^\?/, "");
  const fromQuery = new URLSearchParams(stripped).get("redirect");
  if (fromQuery?.trim()) return safeInternalPath(fromQuery);
  if (stored.trim()) return safeInternalPath(stored);
  return "/tasks";
}

/**
 * Where a real form sign-in goes. The hidden field wins, because a phone
 * password sheet can reload /login and drop the query string.
 */
export function loginFormDestination(
  redirectField: string | null | undefined,
  forcePasswordReset: boolean,
  search = "",
): string {
  if (forcePasswordReset) return "/change-password";
  const raw = redirectField?.trim() || "";
  if (raw) return safeInternalPath(raw);
  return loginLandingPath(false, search);
}

/** Send a failed form sign-in back to the login page without putting the password on the URL. */
export function loginFailurePath(
  error: "invalid" | "missing" | "account",
  redirectField: string | null | undefined,
): string {
  const params = new URLSearchParams();
  params.set("error", error);
  const raw = redirectField?.trim() || "";
  if (raw && safeInternalPath(raw) === raw) params.set("redirect", raw);
  return `/login?${params.toString()}`;
}

/** Survives a same-tab reload that strips ?redirect= from the login address. */
export const LOGIN_REDIRECT_STORAGE_KEY = "connectz-login-redirect";

export const LOGIN_REDIRECT_BOOT_SCRIPT =
  `(function(){try{var q=new URLSearchParams(location.search).get("redirect");` +
  `if(!q||q.charAt(0)!=="/"||q.charAt(1)==="/")return;` +
  `sessionStorage.setItem(${JSON.stringify(LOGIN_REDIRECT_STORAGE_KEY)},JSON.stringify({path:q,at:Date.now()}))}catch(e){}})();`;

export function readStoredLoginRedirect(maxAgeMs = 15 * 60 * 1000): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { path?: string; at?: number };
    if (!parsed.path || typeof parsed.at !== "number") return "";
    if (Date.now() - parsed.at > maxAgeMs) return "";
    const dest = safeInternalPath(parsed.path);
    return dest === parsed.path ? dest : "";
  } catch {
    return "";
  }
}

/** Relative app paths only — never `//host` or a full URL. */
export function safeInternalPath(raw: string | null | undefined): string {
  const dest = stripPasswordQuery(raw?.trim() || "/tasks");
  if (dest === "/" || dest === "/login" || dest.startsWith("/login?")) return "/tasks";
  if (!dest.startsWith("/") || dest.startsWith("//") || dest.includes("\\")) return "/tasks";
  if (dest.includes("://")) return "/tasks";
  return dest;
}

export function isAbortError(err: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" && err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

/** Phone networks and a compiling login route must not leave Sign In pending forever. */
export const LOGIN_FETCH_TIMEOUT_MS = 20_000;
